import { Answer } from "@coffee/data-access/api-types";
import { Card } from "@coffee/ui/components/cards";
import { UnlikelyAISpinner } from "@coffee/ui/components/spinners";
import styled from "@emotion/styled";
import { ChatBubbleLeftRightIcon } from "@heroicons/react/24/solid";
import {
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  format,
} from "date-fns";
import { Icon } from "@unlikelyai-magic/ui/icons";
import { Heading } from "@unlikelyai-magic/ui/typography";

const QuestionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  align-items: center;
  width: 100%;
  height: 100%;
  overflow-y: auto;
`;

const NoQuestionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  align-items: center;
  justify-content: center;
`;

const SpinnerContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 40%;
  width: 100%;
`;

const QuestionCard = styled(Card)`
  width: 100%;
`;

type PreviousQuestionsProps = {
  answers: Answer[];
  isLoading: boolean;
  onSelect: (answer: Answer) => void;
};

export const PreviousQuestions = ({
  answers,
  isLoading,
  onSelect,
}: PreviousQuestionsProps) => {
  const getAnswerDescription = (answer: Answer) => {
    const createdAt = new Date(answer.created_at);
    const now = new Date();
    const minutes = differenceInMinutes(now, createdAt);
    const hours = differenceInHours(now, createdAt);
    const days = differenceInDays(now, createdAt);
    let description = "";
    if (minutes < 60) {
      description = `Asked ${minutes} minute${minutes === 1 ? "" : "s"} ago`;
    } else if (hours < 24) {
      description = `Asked ${hours} hour${hours === 1 ? "" : "s"} ago`;
    } else if (days <= 10) {
      description = `Asked ${days} day${days === 1 ? "" : "s"} ago`;
    } else {
      description = `Asked on ${format(createdAt, "dd/MM/yyyy")}`;
    }
    return description;
  };

  if (isLoading) {
    return (
      <SpinnerContainer>
        <UnlikelyAISpinner />
      </SpinnerContainer>
    );
  }

  if (answers.length === 0) {
    return (
      <NoQuestionsContainer>
        <Icon icon={ChatBubbleLeftRightIcon} size={"10rem"} />
        <Heading level="h5">
          No previous questions found. Ask a question to get started!
        </Heading>
      </NoQuestionsContainer>
    );
  }

  return (
    <QuestionsContainer>
      {answers
        .slice()
        .sort(
          (a, b) =>
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        )
        .map((answer) => (
          <QuestionCard key={answer.id} onClick={() => onSelect(answer)}>
            <Card.Header
              title={answer.question}
              description={getAnswerDescription(answer)}
            />
          </QuestionCard>
        ))}
    </QuestionsContainer>
  );
};
